import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { sendSubmissionLinkEmail } from '../services/emailService';
import SubmissionLinkEmailTemplateEditor from './SubmissionLinkEmailTemplateEditor';
import '../styles/SubmissionLinkManager.css';

const SubmissionLinkManager = () => {
  const { currentUser } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [generatedLinks, setGeneratedLinks] = useState({});
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [conferenceStatusFilter, setConferenceStatusFilter] = useState('all');
  const [statusConfig, setStatusConfig] = useState(null);
  const [showEmailTemplateEditor, setShowEmailTemplateEditor] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name, status, district, lastUpdated
  const [sortDirection, setSortDirection] = useState('asc'); // asc or desc

  // Get user's districts for dCOM/dCOM Member filtering
  const userDistrictIds = currentUser?.districtIds || (currentUser?.districtId ? [currentUser.districtId] : []);
  const userRole = currentUser?.role;
  const isDCOM = userRole === 'dCOM' || userRole === 'dCOM Member';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchStatusConfig();
    fetchDistricts();
    fetchCandidatesAndSubmissions();
  }, [selectedYear]);

  const fetchStatusConfig = async () => {
    try {
      const configDoc = await getDoc(doc(db, 'config', 'statusConfig'));
      if (configDoc.exists()) {
        setStatusConfig(configDoc.data());
      }
    } catch (error) {
      console.error('Error fetching status config:', error);
    }
  };

  const fetchDistricts = async () => {
    try {
      const districtsRef = collection(db, 'districts');
      const snapshot = await getDocs(districtsRef);
      
      const districtsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDistricts(districtsList);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchCandidatesAndSubmissions = async () => {
    try {
      // Fetch all candidates
      const candidatesRef = collection(db, 'candidates');
      const candidatesSnapshot = await getDocs(candidatesRef);
      const candidatesList = candidatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch submissions for the selected year
      const submissionsRef = collection(db, 'candidateSubmissions');
      let submissionsList = [];
      
      try {
        const submissionsQuery = query(
          submissionsRef,
          where('year', '==', selectedYear)
        );
        const submissionsSnapshot = await getDocs(submissionsQuery);
        submissionsList = submissionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (submissionError) {
        console.log('No submissions found for year:', selectedYear);
        // This is okay - there might not be any submissions yet
      }

      setCandidates(candidatesList);
      setSubmissions(submissionsList);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSubmissionToken = (candidateId, year) => {
    return btoa(`${candidateId}-${year}-submission`);
  };

  const generateSubmissionLink = (candidateId) => {
    const token = generateSubmissionToken(candidateId, selectedYear);
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/submission/${candidateId}?year=${selectedYear}&token=${token}`;
    
    setGeneratedLinks(prev => ({
      ...prev,
      [candidateId]: link
    }));
    
    return link;
  };

  const copyToClipboard = async (text, candidateId) => {
    try {
      await navigator.clipboard.writeText(text);
      // Show temporary feedback
      const button = document.getElementById(`copy-btn-${candidateId}`);
      const originalText = button.textContent;
      button.textContent = 'Copied!';
      button.style.backgroundColor = '#28a745';
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = '';
      }, 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      alert('Failed to copy link. Please copy manually.');
    }
  };

  const getSubmissionStatus = (candidateId) => {
    const submission = submissions.find(s => s.candidateId === candidateId);
    if (!submission) return { status: 'not-started', text: 'Not Started' };
    
    return {
      status: submission.status,
      text: submission.status === 'submitted' ? 'Submitted' : 'Draft',
      date: submission.submittedAt || submission.lastUpdated
    };
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Show more years: 5 years back to 3 years forward
    for (let i = currentYear - 5; i <= currentYear + 3; i++) {
      years.push(i);
    }
    return years;
  };


  const getConferenceStatusLabel = (status) => {
    if (statusConfig && statusConfig.conferenceStatuses) {
      const statusObj = statusConfig.conferenceStatuses.find(s => s.id === status);
      if (statusObj) return statusObj.label;
    }
    // Fallback labels
    const fallbackLabels = {
      'lay': 'Lay - In Process',
      'clm': 'CLM',
      'cmc': 'Certified Candidate',
      'supply-pastor': 'Supply Pastor',
      'local-pastor': 'Local Pastor',
      'retired-local': 'Retired Local Pastor'
    };
    return fallbackLabels[status] || status;
  };

  // Bulk selection functions
  const handleSelectCandidate = (candidateId, isSelected) => {
    const newSelected = new Set(selectedCandidates);
    if (isSelected) {
      newSelected.add(candidateId);
    } else {
      newSelected.delete(candidateId);
    }
    setSelectedCandidates(newSelected);
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const allCandidateIds = filteredCandidates.map(c => c.id);
      setSelectedCandidates(new Set(allCandidateIds));
    } else {
      setSelectedCandidates(new Set());
    }
  };

  const generateBulkLinks = async () => {
    setBulkGenerating(true);
    const selectedArray = Array.from(selectedCandidates);
    
    try {
      // Generate links for all selected candidates
      const newLinks = {};
      selectedArray.forEach(candidateId => {
        const token = generateSubmissionToken(candidateId, selectedYear);
        const baseUrl = window.location.origin;
        const link = `${baseUrl}/submission/${candidateId}?year=${selectedYear}&token=${token}`;
        newLinks[candidateId] = link;
      });

      setGeneratedLinks(prev => ({
        ...prev,
        ...newLinks
      }));

      // Show success message
      alert(`Successfully generated ${selectedArray.length} submission links!`);
    } catch (error) {
      console.error('Error generating bulk links:', error);
      alert('Error generating links. Please try again.');
    } finally {
      setBulkGenerating(false);
    }
  };

  const copyAllSelectedLinks = async () => {
    const selectedArray = Array.from(selectedCandidates);
    const linksWithNames = selectedArray
      .filter(candidateId => generatedLinks[candidateId])
      .map(candidateId => {
        const candidate = candidates.find(c => c.id === candidateId);
        return `${candidate.firstName} ${candidate.lastName}: ${generatedLinks[candidateId]}`;
      });

    if (linksWithNames.length === 0) {
      alert('No generated links found for selected candidates.');
      return;
    }

    const allLinksText = linksWithNames.join('\n');
    
    try {
      await navigator.clipboard.writeText(allLinksText);
      alert(`Copied ${linksWithNames.length} links to clipboard!`);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      alert('Failed to copy links. Please copy manually.');
    }
  };

  // Email sending functions
  const sendEmailToCandidate = async (candidateId) => {
    const candidate = candidates.find(c => c.id === candidateId);
    const submissionLink = generatedLinks[candidateId];
    
    if (!candidate || !submissionLink) {
      alert('Please generate the submission link first.');
      return;
    }

    if (!candidate.email) {
      alert('This candidate does not have an email address on file.');
      return;
    }

    setSendingEmails(prev => new Set(prev).add(candidateId));
    
    try {
      await sendSubmissionLinkEmail(candidate, submissionLink, selectedYear);
      alert(`Email sent successfully to ${candidate.firstName} ${candidate.lastName}!`);
    } catch (error) {
      console.error('Error sending email:', error);
      
      // Handle fallback for undeployed functions
      if (error.message === 'Email functions not deployed yet' && error.details) {
        const shouldCopy = window.confirm(
          `${error.message}\n\n${error.details}\n\nWould you like to copy the submission link to your clipboard?`
        );
        
        if (shouldCopy) {
          try {
            await navigator.clipboard.writeText(submissionLink);
            alert(`Submission link copied to clipboard!\n\nCandidate: ${candidate.firstName} ${candidate.lastName}\nEmail: ${candidate.email}`);
          } catch (clipboardError) {
            alert(`Manual copy required:\n\nLink: ${submissionLink}\nCandidate: ${candidate.firstName} ${candidate.lastName}\nEmail: ${candidate.email}`);
          }
        }
      } else {
        alert(`Failed to send email to ${candidate.firstName} ${candidate.lastName}. Please try again.`);
      }
    } finally {
      setSendingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(candidateId);
        return newSet;
      });
    }
  };

  const sendBulkEmails = async () => {
    const selectedArray = Array.from(selectedCandidates);
    const candidatesWithLinks = selectedArray.filter(candidateId => generatedLinks[candidateId]);
    
    if (candidatesWithLinks.length === 0) {
      alert('Please generate submission links for the selected candidates first.');
      return;
    }

    const candidatesWithEmails = candidatesWithLinks.filter(candidateId => {
      const candidate = candidates.find(c => c.id === candidateId);
      return candidate && candidate.email;
    });

    if (candidatesWithEmails.length === 0) {
      alert('None of the selected candidates have email addresses on file.');
      return;
    }

    const confirmSend = window.confirm(
      `Send submission link emails to ${candidatesWithEmails.length} candidate(s)?`
    );

    if (!confirmSend) return;

    setSendingEmails(prev => new Set([...prev, ...candidatesWithEmails]));
    
    let successCount = 0;
    let errorCount = 0;
    let functionsNotDeployed = false;

    for (const candidateId of candidatesWithEmails) {
      try {
        const candidate = candidates.find(c => c.id === candidateId);
        const submissionLink = generatedLinks[candidateId];
        await sendSubmissionLinkEmail(candidate, submissionLink, selectedYear);
        successCount++;
      } catch (error) {
        console.error(`Error sending email to candidate ${candidateId}:`, error);
        
        if (error.message === 'Email functions not deployed yet') {
          functionsNotDeployed = true;
        }
        errorCount++;
      }
    }

    setSendingEmails(new Set());
    
    if (functionsNotDeployed) {
      const linksText = candidatesWithEmails
        .map(candidateId => {
          const candidate = candidates.find(c => c.id === candidateId);
          const link = generatedLinks[candidateId];
          return `${candidate.firstName} ${candidate.lastName} (${candidate.email}): ${link}`;
        })
        .join('\n\n');
      
      const shouldCopy = window.confirm(
        `Email functions not deployed yet. To enable email sending:\n\n1. Run: firebase deploy --only functions\n2. This will deploy the required email functions\n3. Email functionality will then work immediately\n\nWould you like to copy all the submission links to send manually?`
      );
      
      if (shouldCopy) {
        try {
          await navigator.clipboard.writeText(linksText);
          alert(`All ${candidatesWithEmails.length} submission links copied to clipboard!`);
        } catch (clipboardError) {
          alert(`Manual copy required:\n\n${linksText}`);
        }
      }
    } else if (errorCount === 0) {
      alert(`Successfully sent emails to all ${successCount} candidates!`);
    } else {
      alert(`Sent ${successCount} emails successfully. ${errorCount} emails failed to send.`);
    }
  };

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  // Filter and sort candidates
  const filteredAndSortedCandidates = candidates
    .filter(candidate => {
      // Apply role-based district filtering for dCOM users
      if (isDCOM) {
        if (userDistrictIds.length > 0) {
          const userDistrictNames = userDistrictIds.map(id => {
            const district = districts.find(d => d.id === id);
            return district ? district.name : null;
          }).filter(name => name !== null);
          
          if (userDistrictNames.length > 0) {
            if (!userDistrictNames.includes(candidate.district)) {
              return false;
            }
          } else {
            return false;
          }
        } else {
          return false;
        }
      }

      // Filter by conference status
      if (conferenceStatusFilter !== 'all') {
        if (candidate.conferenceStatus !== conferenceStatusFilter) {
          return false;
        }
      }

      // Filter by search term
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const fullName = `${candidate.firstName} ${candidate.lastName}`.toLowerCase();
        if (!fullName.includes(search)) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      let compareValue = 0;
      
      switch (sortBy) {
        case 'name':
          const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
          const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
          compareValue = nameA.localeCompare(nameB);
          break;
          
        case 'status': {
          const statusA = getSubmissionStatus(a.id).status;
          const statusB = getSubmissionStatus(b.id).status;
          const statusOrder = { 'submitted': 0, 'draft': 1, 'not-started': 2 };
          compareValue = statusOrder[statusA] - statusOrder[statusB];
          break;
        }
          
        case 'district':
          const districtA = (a.district || '').toLowerCase();
          const districtB = (b.district || '').toLowerCase();
          compareValue = districtA.localeCompare(districtB);
          break;
          
        case 'lastUpdated': {
          const dateA = getSubmissionStatus(a.id).date;
          const dateB = getSubmissionStatus(b.id).date;
          if (!dateA && !dateB) compareValue = 0;
          else if (!dateA) compareValue = 1;
          else if (!dateB) compareValue = -1;
          else compareValue = dateA.seconds - dateB.seconds;
          break;
        }
          
        default:
          compareValue = 0;
      }
      
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });

  const filteredCandidates = filteredAndSortedCandidates;

  const isAllSelected = filteredCandidates.length > 0 && selectedCandidates.size === filteredCandidates.length;
  const isPartiallySelected = selectedCandidates.size > 0 && selectedCandidates.size < filteredCandidates.length;

  if (loading) {
    return (
      <div className="submission-link-manager">
        <div className="loading-spinner">Loading candidates and submissions...</div>
      </div>
    );
  }

  return (
    <div className="submission-link-manager">
      <div className="manager-header">
        <div className="header-title">
          <h2>Candidate Submission Links</h2>
          {isDCOM && userDistrictIds.length > 0 && (
            <div className="district-indicator">
              <span className="district-label">
                Viewing: {userDistrictIds.map(id => {
                  const district = districts.find(d => d.id === id);
                  return district ? district.name : 'Unknown';
                }).join(', ')}
              </span>
            </div>
          )}
        </div>
        <div className="header-controls">
          <div className="year-selector">
            <label>Submission Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {getYearOptions().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setShowEmailTemplateEditor(true)}
            className="edit-template-btn"
            title="Edit Email Template"
          >
            Edit Email Template
          </button>
          <a 
            href="/submission-template-editor" 
            className="edit-template-btn"
            title="Edit Submission Portal Template"
          >
            Edit Portal Template
          </a>
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>Search by Name:</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search candidates..."
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <label>Sort By:</label>
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setSortDirection('asc');
            }}
            className="filter-select"
          >
            <option value="name">Name</option>
            <option value="status">Status</option>
            <option value="district">District</option>
            <option value="lastUpdated">Last Updated</option>
          </select>
          <button 
            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            className="sort-direction-btn"
            title={`Sort ${sortDirection === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            {sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>
        <div className="filter-group">
          <label>Filter by Conference Status:</label>
          <select
            value={conferenceStatusFilter}
            onChange={(e) => setConferenceStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Candidates</option>
            {statusConfig && statusConfig.conferenceStatuses ?
              statusConfig.conferenceStatuses
                .filter(status => status.enabled)
                .map(status => (
                  <option key={status.id} value={status.id}>
                    {status.label}
                  </option>
                )) :
              // Fallback options if config not loaded
              [
                <option key="lay" value="lay">Lay: In Process</option>,
                <option key="clm" value="clm">CLM</option>,
                <option key="cmc" value="cmc">Certified Minsitry Candidate</option>,
                <option key="supply-pastor" value="supply-pastor">Supply Pastor</option>,
                <option key="local-pastor" value="local-pastor">Local Pastor</option>,
                <option key="retired-local" value="retired-local">Retired Local Pastor</option>
              ]
            }
          </select>
        </div>
      </div>

      <div className="instructions">
        <h3>Instructions</h3>
        <p>Generate unique submission links for each candidate. These links allow candidates to submit their annual requirements including sermon links, disciplinary questions, and personal statements.</p>
        <ul>
          <li>Each link is unique to the candidate and submission year</li>
          <li>Links include security tokens to prevent unauthorized access</li>
          <li>Candidates can save drafts and submit when ready</li>
          <li>You can track submission status in real-time</li>
        </ul>
      </div>

      {/* Bulk Actions Bar */}
      {selectedCandidates.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="selection-info">
            <span>{selectedCandidates.size} candidate{selectedCandidates.size !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="bulk-actions">
            <button
              onClick={generateBulkLinks}
              disabled={bulkGenerating}
              className="bulk-generate-btn"
            >
              {bulkGenerating ? 'Generating...' : `Generate ${selectedCandidates.size} Links`}
            </button>
            <button
              onClick={sendBulkEmails}
              className="bulk-email-btn"
              disabled={Array.from(selectedCandidates).every(id => !generatedLinks[id]) || sendingEmails.size > 0}
            >
              {sendingEmails.size > 0 ? 'Sending...' : `Email ${selectedCandidates.size} Links`}
            </button>
            <button
              onClick={copyAllSelectedLinks}
              className="bulk-copy-btn"
              disabled={Array.from(selectedCandidates).every(id => !generatedLinks[id])}
            >
              Copy All Selected Links
            </button>
            <button
              onClick={() => setSelectedCandidates(new Set())}
              className="clear-selection-btn"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      <div className="candidates-table">
        <table>
          <thead>
            <tr>
              <th className="checkbox-column">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={input => {
                    if (input) input.indeterminate = isPartiallySelected;
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  title="Select all candidates"
                />
              </th>
              <th>Candidate Name</th>
              <th>Conference Status</th>
              <th>District</th>
              <th>Status</th>
              <th>Last Updated</th>
              <th>Submission Link</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCandidates.map(candidate => {
              const submissionStatus = getSubmissionStatus(candidate.id);
              const hasGeneratedLink = generatedLinks[candidate.id];
              const isSelected = selectedCandidates.has(candidate.id);
              
              return (
                <tr key={candidate.id} className={isSelected ? 'selected' : ''}>
                  <td className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleSelectCandidate(candidate.id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <div className="candidate-name">
                      {candidate.firstName} {candidate.lastName}
                    </div>
                  </td>
                  <td>
                    <span className={`conference-status-badge ${candidate.conferenceStatus?.toLowerCase().replace(/\s+/g, '-')}`}>
                      {getConferenceStatusLabel(candidate.conferenceStatus) || 'Not specified'}
                    </span>
                  </td>
                  <td>{candidate.district || 'Not specified'}</td>
                  <td>
                    <span className={`status-badge ${submissionStatus.status}`}>
                      {submissionStatus.text}
                    </span>
                  </td>
                  <td>
                    {submissionStatus.date ? 
                      new Date(submissionStatus.date.seconds * 1000).toLocaleDateString() : 
                      'N/A'
                    }
                  </td>
                  <td>
                    {hasGeneratedLink ? (
                      <div className="link-display">
                        <input
                          type="text"
                          value={generatedLinks[candidate.id]}
                          readOnly
                          className="link-input"
                        />
                      </div>
                    ) : (
                      <span className="no-link">Click "Generate Link" to create</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {!hasGeneratedLink ? (
                        <button
                          onClick={() => generateSubmissionLink(candidate.id)}
                          className="generate-btn"
                        >
                          Generate Link
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => sendEmailToCandidate(candidate.id)}
                            className="email-btn"
                            disabled={sendingEmails.has(candidate.id) || !candidate.email}
                            title={!candidate.email ? 'No email address on file' : 'Send submission link via email'}
                          >
                            {sendingEmails.has(candidate.id) ? 'Sending...' : '📧 Email'}
                          </button>
                          <button
                            id={`copy-btn-${candidate.id}`}
                            onClick={() => copyToClipboard(generatedLinks[candidate.id], candidate.id)}
                            className="copy-btn"
                          >
                            Copy Link
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredCandidates.length === 0 && (
        <div className="no-candidates">
          <p>
            {candidates.length === 0 
              ? "No candidates found. Add candidates to generate submission links."
              : `No candidates found matching the selected filter "${conferenceStatusFilter}".`
            }
          </p>
        </div>
      )}

      <div className="summary-stats">
        <h3>Submission Summary for {selectedYear}</h3>
        {conferenceStatusFilter !== 'all' && (
          <p className="filter-note">Showing results for: <strong>{conferenceStatusFilter}</strong></p>
        )}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{filteredCandidates.length}</div>
            <div className="stat-label">
              {conferenceStatusFilter === 'all' ? 'Total Candidates' : 'Filtered Candidates'}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {submissions.filter(s => {
                const candidate = candidates.find(c => c.id === s.candidateId);
                if (!candidate) return false;
                
                // Apply district filtering for dCOM users
                if (isDCOM) {
                  if (userDistrictIds.length > 0) {
                    const userDistrictNames = userDistrictIds.map(id => {
                      const district = districts.find(d => d.id === id);
                      return district ? district.name : null;
                    }).filter(name => name !== null);
                    
                    if (userDistrictNames.length > 0 && !userDistrictNames.includes(candidate.district)) {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }
                
                const matchesFilter = conferenceStatusFilter === 'all' || candidate?.conferenceStatus === conferenceStatusFilter;
                return s.status === 'submitted' && matchesFilter;
              }).length}
            </div>
            <div className="stat-label">Submitted</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {submissions.filter(s => {
                const candidate = candidates.find(c => c.id === s.candidateId);
                if (!candidate) return false;
                
                // Apply district filtering for dCOM users
                if (isDCOM) {
                  if (userDistrictIds.length > 0) {
                    const userDistrictNames = userDistrictIds.map(id => {
                      const district = districts.find(d => d.id === id);
                      return district ? district.name : null;
                    }).filter(name => name !== null);
                    
                    if (userDistrictNames.length > 0 && !userDistrictNames.includes(candidate.district)) {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }
                
                const matchesFilter = conferenceStatusFilter === 'all' || candidate?.conferenceStatus === conferenceStatusFilter;
                return s.status === 'draft' && matchesFilter;
              }).length}
            </div>
            <div className="stat-label">In Progress</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {filteredCandidates.length - submissions.filter(s => {
                const candidate = candidates.find(c => c.id === s.candidateId);
                if (!candidate) return false;
                
                // Apply district filtering for dCOM users
                if (isDCOM) {
                  if (userDistrictIds.length > 0) {
                    const userDistrictNames = userDistrictIds.map(id => {
                      const district = districts.find(d => d.id === id);
                      return district ? district.name : null;
                    }).filter(name => name !== null);
                    
                    if (userDistrictNames.length > 0 && !userDistrictNames.includes(candidate.district)) {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }
                
                const matchesFilter = conferenceStatusFilter === 'all' || candidate?.conferenceStatus === conferenceStatusFilter;
                return matchesFilter;
              }).length}
            </div>
            <div className="stat-label">Not Started</div>
          </div>
        </div>
      </div>

      {/* Email Template Editor Modal */}
      <SubmissionLinkEmailTemplateEditor
        isOpen={showEmailTemplateEditor}
        onClose={() => setShowEmailTemplateEditor(false)}
      />
    </div>
  );
};

export default SubmissionLinkManager;
